import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const stats = [
  { value: '50+', label: 'WEB制作実績' },
  { value: '90%', label: 'リピート・紹介率' },
  { value: '全国', label: '対応エリア' },
];

export default function Hero() {
  const headRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 120);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #020B18 0%, #061428 40%, #0A1E3D 70%, #0D2550 100%)' }}
    >
      {/* Background Video */}
      <div className="absolute inset-0">
        <video
          src="https://public.readdy.ai/ai/video_res/019d4f23-2cf5-7a31-8878-5d20ea2468d9.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020B18]/95 via-[#061428]/75 to-[#020B18]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020B18] via-transparent to-[#020B18]/60" />
      </div>

      {/* Animated grid texture */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: 'linear-gradient(#1E6FFF 1px, transparent 1px), linear-gradient(90deg, #1E6FFF 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      {/* Glowing orbs */}
      <div
        className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(30,111,255,0.15) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      <div
        className="absolute bottom-1/3 left-1/5 w-64 h-64 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0,200,255,0.10) 0%, transparent 70%)',
          filter: 'blur(30px)',
        }}
      />

      {/* Left accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#1E6FFF]/40 to-transparent" />

      {/* Content */}
      <div
        ref={headRef}
        className={`relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-28 transition-all duration-1000 ease-out ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left: Main Copy */}
          <div className="lg:col-span-7">
            <div
              className={`transition-all duration-700 delay-100 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <p className="text-[#4DA3FF] text-xs tracking-[0.45em] mb-7 uppercase flex items-center gap-3">
                <span className="inline-block w-6 h-px bg-[#1E6FFF]" />
                Web Design &amp; Digital Marketing — Osaka
              </p>
            </div>

            <div
              className={`transition-all duration-700 delay-200 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <h1 className="text-white font-bold leading-[1.08] mb-8 tracking-tight">
                <span className="block text-4xl md:text-6xl lg:text-7xl mb-1">
                  デザインだけじゃない。
                </span>
                <span
                  className="block text-4xl md:text-6xl lg:text-7xl"
                  style={{
                    background: 'linear-gradient(90deg, #4DA3FF 0%, #00C8FF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  成果を生む
                </span>
                <span className="block text-4xl md:text-6xl lg:text-7xl">
                  WEB戦略を。
                </span>
              </h1>
            </div>

            <div
              className={`w-16 h-px mb-7 transition-all duration-700 delay-300 ${
                visible ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ background: 'linear-gradient(90deg, #1E6FFF, transparent)' }}
            />

            <div
              className={`transition-all duration-700 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <p className="text-white/60 text-base md:text-lg leading-relaxed mb-10 font-light max-w-lg">
                WEBサイト制作・SNS運用代行・MEO対策——<br />
                ひとりの担当が一気通貫で伴走します。<br />
                大阪を拠点に、全国対応可能です。
              </p>
            </div>

            <div
              className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-[400ms] ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <Link
                to="/contact"
                className="text-white px-10 py-5 rounded-full text-sm tracking-widest font-bold transition-all duration-300 whitespace-nowrap text-center hover:opacity-90 hover:scale-105"
                style={{ background: 'linear-gradient(90deg, #1E6FFF 0%, #0099FF 100%)' }}
              >
                無料相談を申し込む
              </Link>
              <Link
                to="/services"
                className="border border-white/30 text-white px-10 py-5 rounded-full text-sm tracking-widest hover:bg-white/10 hover:border-white/50 transition-all duration-300 whitespace-nowrap text-center"
              >
                サービスを見る
              </Link>
            </div>
          </div>

          {/* Right: Stats Cards */}
          <div
            className={`lg:col-span-5 grid grid-cols-2 lg:grid-cols-1 gap-4 transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            {stats.map((s) => (
              <div
                key={s.label}
                className="backdrop-blur-sm rounded-2xl px-6 py-6 hover:bg-white/[0.10] transition-colors"
                style={{
                  background: 'rgba(30,111,255,0.08)',
                  border: '1px solid rgba(30,111,255,0.25)',
                }}
              >
                <p
                  className="text-3xl md:text-4xl font-bold mb-1 tracking-tight"
                  style={{
                    background: 'linear-gradient(90deg, #4DA3FF, #00C8FF)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {s.value}
                </p>
                <p className="text-white/50 text-xs tracking-widest">{s.label}</p>
              </div>
            ))}

            {/* Trust badge */}
            <div
              className="col-span-2 lg:col-span-1 rounded-2xl px-6 py-6"
              style={{
                background: 'rgba(30,111,255,0.12)',
                border: '1px solid rgba(30,111,255,0.35)',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-7 h-7 flex items-center justify-center rounded-full"
                  style={{ background: 'rgba(30,111,255,0.25)' }}
                >
                  <i className="ri-shield-check-line text-[#4DA3FF] text-sm" />
                </div>
                <p className="text-[#4DA3FF] text-xs tracking-widest font-medium">初回相談 無料</p>
              </div>
              <p className="text-white/50 text-xs leading-relaxed">
                費用・スケジュール・ご要望など、<br />気軽にご相談ください。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className={`absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10 transition-all duration-700 delay-700 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <span className="text-white/30 text-[10px] tracking-[0.3em]">SCROLL</span>
        <div className="w-px h-12 bg-gradient-to-b from-[#1E6FFF]/50 to-transparent relative overflow-hidden">
          <div
            className="absolute top-0 left-0 w-full bg-[#4DA3FF] rounded-full"
            style={{
              height: '40%',
              animation: 'scrollDrop 1.8s ease-in-out infinite',
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes scrollDrop {
          0%   { transform: translateY(-100%); opacity: 0; }
          30%  { opacity: 1; }
          100% { transform: translateY(300%); opacity: 0; }
        }
      `}</style>
    </section>
  );
}